"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, Mail, ClipboardCheck, Truck, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const orderSteps = [{
  stepNumber: "01",
  title: "1. Choose a Machine",
  description: "Browse the current New or Used inventory and open the product page for the machine you are considering.",
  icon: Search
}, {
  stepNumber: "02",
  title: "2. Send an Enquiry",
  description: "Use the enquiry button and include the product name or SKU together with any questions you have.",
  icon: Mail
}, {
  stepNumber: "03",
  title: "3. Confirm Product Information",
  description: "Review the available price, condition, included equipment and product-specific information before proceeding.",
  icon: ClipboardCheck
}, {
  stepNumber: "04",
  title: "4. Confirm Delivery Details",
  description: "Provide the destination information needed to review the available shipping arrangement.",
  icon: Truck
}, {
  stepNumber: "05",
  title: "5. Complete the Order",
  description: "Follow the order and payment instructions provided for that specific purchase once the product and delivery information have been confirmed.",
  icon: CheckCircle2
}];
export default function Ordering() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        duration: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="ordering-payment" data-nav="light" className="relative w-full border-t border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Header Spec Block */}
        <div className="border-b border-border pb-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-serif text-3xl font-bold tracking-tight uppercase text-foreground md:text-5xl">
                How to Order a Mechanical Bull
              </h2>
            </div>
            <p className="max-w-xl font-sans text-sm text-muted-foreground md:text-base">
              Complete mechanical bulls use an enquiry-based ordering process so product and delivery details can be reviewed before the order proceeds.
            </p>
          </div>
        </div>

        {/* 3-Column Procedure Grid */}
        <motion.div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }}>
          {orderSteps.map((step, i) => {
          const Icon = step.icon;
          return <motion.div key={step.stepNumber} data-index={i} variants={itemVariants} className="h-full">
                <Card className="relative flex h-full flex-col justify-between rounded-lg border border-border bg-card p-6 transition-[border-color] duration-180 hover:border-foreground">
                  <div>
                    {/* Step Number & Icon Header */}
                    <div className="flex items-center justify-between border-b border-border pb-4">
                      <span className="font-serif text-2xl font-bold text-primary">
                        {step.stepNumber}
                      </span>
                      <div className="flex size-10 items-center justify-center rounded-lg border border-border bg-muted text-foreground">
                        <Icon className="size-5" />
                      </div>
                    </div>

                    {/* Step Title */}
                    <div className="mt-6">
                      <h3 className="font-serif text-xl font-semibold uppercase tracking-wide text-foreground">
                        {step.title}
                      </h3>
                    </div>

                    {/* Description */}
                    <p className="mt-4 font-sans text-sm leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Action Plate */}
        <div className="mt-12 border border-border bg-muted/50 p-6 md:p-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-serif text-2xl font-bold uppercase tracking-tight text-foreground">
                Explore Available Units
              </h3>
              <p className="mt-1 font-sans text-sm text-muted-foreground">
                Review available inventory and open any model page to submit an enquiry.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button asChild className="rounded-lg border border-primary bg-primary px-6 font-serif uppercase tracking-wider text-primary-foreground hover:bg-primary/90">
                <Link to="/NewMechanicalBullsPage">
                  Shop New Bulls
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-lg border-border font-serif uppercase tracking-wider text-foreground hover:bg-background">
                <Link to="/UsedMechanicalBullsPage">
                  Shop Used Bulls
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}